import { useCallback } from "react";
import confetti from "canvas-confetti";

import API from "../../../utils/axios";

import { useTourMethods } from "../index";
import {
  getStepBeforeShowPromiseWithCheck,
  getStepWhenWithPulsingBorder,
  getStepButtons,
  preventEvent,
  allowMouseEvents
} from "../utils";
import { TOUR_TYPES } from "../constants";
import { store } from "../../..";
import { setUser } from "../../../store/actions/auth";

/*
  Different product tour step IDs. Each step should have an ID from this object,
  so we can use a single source for the step ids, during the tour for checking
  current step ID, or updating a step options by ID.
 */
const PRODUCT_TOUR_STEP_IDS = {
  PART_MISC_STEP_ONBOARDING_WELCOME: "part_misc_step_onboarding_welcome",
  PART_INITIALIZE_NEW_PROJECT_STEP_NEW_PROJECT_BUTTON:
    "part_initialize_new_project_step_new_project_button",
  PART_INITIALIZE_NEW_PROJECT_STEP_NEW_PROJECT_BRIEF:
    "part_initialize_new_project_step_new_project_brief",
  PART_INITIALIZE_NEW_PROJECT_STEP_SET_TITLE:
    "part_initialize_new_project_step_set_title",
  PART_INITIALIZE_NEW_PROJECT_STEP_SET_DATE:
    "part_initialize_new_project_step_set_date",
  PART_ADDING_STOPS_AND_OPTIMIZE_STEP_BRIEF:
    "part_adding_stops_and_optimize_step_brief",
  PART_ADDING_STOPS_AND_OPTIMIZE_STEP_SEARCH_AND_OPTIMIZE:
    "part_adding_stops_and_optimize_step_search_and_optimize",
  PART_PUBLISH_AND_SHARE_STEP_BRIEF: "part_publish_and_share_step_brief",
  PART_PUBLISH_AND_SHARE_STEP_PUBLISH: "part_publish_and_share_step_publish",
  PART_PUBLISH_AND_SHARE_STEP_SHARE_LINK:
    "part_publish_and_share_step_share_link",
  PART_PUBLISH_AND_SHARE_STEP_SHARE_DIALOG:
    "part_publish_and_share_step_share_dialog",
  PART_PUBLISH_AND_SHARE_STEP_FINAL: "part_publish_and_share_step_final"
};

// Parts and Steps
// Onboarding specific welcome
const gePartMiscStepOnboardingWelcome = (userData, history) => [
  {
    id: PRODUCT_TOUR_STEP_IDS.PART_MISC_STEP_ONBOARDING_WELCOME,
    title: `Welcome, ${userData.firstName}! 🎉`,
    text: `
      It's time to simplify your route planning. By following just a few steps,
      you can create optimal routes that will save you hours each day.
      Sound great right? Let’s get started!
    `,
    ...getStepButtons(
      "Let’s go",
      step => {
        history.push("/projects");
        return step.next();
      },
      "Skip for now"
    )
  }
];

// Part one of onboarding: Create a new project
const getPartOneInitializeNewProjectSteps = () => [
  {
    id: PRODUCT_TOUR_STEP_IDS.PART_INITIALIZE_NEW_PROJECT_STEP_NEW_PROJECT_BUTTON,
    text: `
      The first step is to <b>create a new project</b>.
      Just click on the highlighted button above!
    `,
    attachTo: {
      element: "#add_new_project_button",
      on: "bottom"
    },
    classes: "no-footer",
    ...getStepBeforeShowPromiseWithCheck(
      () =>
        window.location.href.includes("/projects") &&
        document.querySelector("#projects_overview.projects-ready") !== null &&
        document.querySelector("#add_new_project_button")
    ),
    ...getStepWhenWithPulsingBorder({
      targetSelector: "#add_new_project_button",
      onScrollRepositionElementSelector: "#pages_wrapper",
      onShow: () => {
        if (document.querySelector("#pages_wrapper")) {
          document
            .querySelector("#pages_wrapper")
            .addEventListener("mousewheel", preventEvent);
        }
      },
      onHide: () => {
        if (document.querySelector("#pages_wrapper")) {
          document
            .querySelector("#pages_wrapper")
            .removeEventListener("mousewheel", preventEvent);
        }
      }
    })
  },
  {
    id: PRODUCT_TOUR_STEP_IDS.PART_INITIALIZE_NEW_PROJECT_STEP_NEW_PROJECT_BRIEF,
    text: `
      Great, you’ve created your first project! 🎉 A project consists of several
      stops (delivery, pickup, service) you need to make on a particular day.
      Let's start by adding a title to your project.
      <br/>
      <div class='progress-wrapper merge-to-footer'>1 of 5</div>
    `,
    ...getStepBeforeShowPromiseWithCheck(() =>
      document.querySelector(".project-loaded")
    ),
    ...getStepButtons("Next", undefined, "End tour")
  },
  {
    id: PRODUCT_TOUR_STEP_IDS.PART_INITIALIZE_NEW_PROJECT_STEP_SET_TITLE,
    text: `
      Name your project and press the “<b>Enter</b>” key.
      <br/>
      <div class='progress-wrapper'>2 of 5</div>
    `,
    attachTo: {
      element: "#project_title_wrapper",
      on: "bottom"
    },
    modalOverlayOpeningPadding: 5,
    popperOptions: {
      modifiers: [{ name: "offset", options: { offset: [16, 23] } }]
    },
    ...getStepWhenWithPulsingBorder({ overlayPadding: 5 })
  },
  {
    id: PRODUCT_TOUR_STEP_IDS.PART_INITIALIZE_NEW_PROJECT_STEP_SET_DATE,
    text: `
      Select a date on which you’d like to plan your stops and click on
      “<b>Save</b>”.
      <br/>
      <div class='progress-wrapper'>3 of 5</div>
    `,
    attachTo: {
      element: "#project_date_picker_wrapper",
      on: "right-start"
    },
    modalOverlayOpeningPadding: 0,
    modalOverlayOpeningRadius: 0,
    popperOptions: {
      modifiers: [{ name: "offset", options: { offset: [0, 40] } }]
    },
    ...getStepBeforeShowPromiseWithCheck(() =>
      document.querySelector("#project_date_picker_wrapper.entered")
    ),
    ...getStepWhenWithPulsingBorder({
      targetSelector: "#project_date_save_button",
      show: false
    })
  }
];

// Part two of onboarding: Adding stops and optimize
const getPartTwoAddingStopsAndOptimizeSteps = () => [
  {
    id: PRODUCT_TOUR_STEP_IDS.PART_ADDING_STOPS_AND_OPTIMIZE_STEP_BRIEF,
    text: `
      Next, let's add stops to our project. Stops can either be manually entered
      or imported using a spreadsheet. For the purpose of this demo, 
      let’s continue to manually add stops by clicking on the “<b>Add stop</b>” 
      button.
      <br/>
      <div class='progress-wrapper'>4 of 5</div>
    `,
    attachTo: {
      element: "#project_drawer",
      on: "right"
    },
    modalOverlayOpeningPadding: 0,
    modalOverlayOpeningRadius: 0,
    ...getStepBeforeShowPromiseWithCheck(
      () => !document.querySelector("#project_date_picker_wrapper")
    ),
    ...getStepWhenWithPulsingBorder({
      targetSelector: "#new_stop_button"
    })
  },
  {
    id: PRODUCT_TOUR_STEP_IDS.PART_ADDING_STOPS_AND_OPTIMIZE_STEP_SEARCH_AND_OPTIMIZE,
    title: `We’re almost there! 😃`,
    text: `
      Type the stop address and click the one you want to add. Add at least
      two stops, then click on "Optimize routes" to see the magic happen!
      <br/>
      <div class='progress-wrapper'>5 of 5</div>
    `,
    popperOptions: {
      modifiers: [{ name: "offset", options: { offset: [5, 23] } }]
    },
    attachTo: {
      element: "#project_drawer",
      on: "right-start"
    },
    modalOverlayOpeningPadding: 0,
    modalOverlayOpeningRadius: 0,
    ...getStepWhenWithPulsingBorder({
      targetSelector: "#project_drawer_optimize_routes_button",
      show: false,
      borderRadius: "40px"
    })
  }
];

// Part three of onboarding: Publish and share
const getPartThreePublishAndShareSteps = (userData, history, startTour) => [
  {
    id: PRODUCT_TOUR_STEP_IDS.PART_PUBLISH_AND_SHARE_STEP_BRIEF,
    text: `
      That's it, you’ve just created your first optimized route!
      It’s really that simple. 💪 
      <br />
      <br />
      You can change the sequence of stops, edit them, as well as
      reoptimize your routes as many times as you wish.
      Now, let's move on to the final part.
      <br/>
      <div class='progress-wrapper merge-to-footer'>1 of 4</div>
    `,
    ...getStepBeforeShowPromiseWithCheck(() =>
      document.querySelector("#project_drawer_publish_button")
    ),
    ...getStepButtons("Next", undefined, "End tour")
  },
  {
    id: PRODUCT_TOUR_STEP_IDS.PART_PUBLISH_AND_SHARE_STEP_PUBLISH,
    text: `
      Click "Publish routes" to finalize the project so that we can send it to
      our drivers.
      <br/>
      <div class='progress-wrapper'>2 of 4</div>
    `,
    attachTo: {
      element: "#project_drawer_publish_button",
      on: "right"
    },
    ...getStepWhenWithPulsingBorder({
      targetSelector: "#project_drawer_publish_button"
    })
  },
  {
    id: PRODUCT_TOUR_STEP_IDS.PART_PUBLISH_AND_SHARE_STEP_SHARE_LINK,
    text: `
     Great job! We can now share routes with our drivers.
     Click on the "Share"-icon next to a route to find out how. 
      <br/>
      <div class='progress-wrapper'>3 of 4</div>
      `,
    attachTo: {
      element: "#route_groups_wrapper .route-details:first-child",
      on: "right"
    },
    modalOverlayOpeningPadding: 8,
    modalOverlayOpeningRadius: 0,
    ...getStepBeforeShowPromiseWithCheck(() =>
      document.querySelector(
        "#route_groups_wrapper div:first-child .driver-share-link"
      )
    ),
    ...getStepWhenWithPulsingBorder({
      targetSelector: "#route_groups_wrapper .route-details .driver-share-link",
      borderRadius: "100%",
      overlayPadding: 7.5
    })
  },
  {
    id: PRODUCT_TOUR_STEP_IDS.PART_PUBLISH_AND_SHARE_STEP_SHARE_DIALOG,
    text: `
      You can copy the route link to your clipboard to share it, or you can 
      send it directly to your driver's e-mail from here. For now, enter your 
      own e-mail and click “<b>Send</b>” to receive your first route!
      <br/>
      <div class='progress-wrapper'>4 of 4</div>
    `,
    attachTo: {
      element: "#share_with_driver_dialog",
      on: "right"
    },
    modalOverlayOpeningPadding: 0,
    ...getStepBeforeShowPromiseWithCheck(() =>
      document.querySelector("#share_with_driver_dialog")
    ),
    ...getStepWhenWithPulsingBorder({
      targetSelector: "#share_with_driver_send_button",
      show: false,
      overlayPadding: 7
    })
  },
  {
    id: PRODUCT_TOUR_STEP_IDS.PART_PUBLISH_AND_SHARE_STEP_FINAL,
    text: `
     <b>Congratulations on your first project!</b> Continue by creating a 
     new project or go back to the previous one to get an idea of how live 
     tracking works by starting the route you just received by email.
      <br/>
    `,
    when: {
      show: () => {
        allowMouseEvents();
        // Show confetti when product tour finished.
        const totalParticleCount = 1000;
        const defaultConfig = {
          origin: { y: -0.4 },
          angle: -90,
          scalar: 1.6,
          zIndex: 9998,
          spread: 180,
          decay: 0.92
        };

        function fire(particleRatio, opts) {
          confetti(
            Object.assign({}, defaultConfig, opts, {
              particleCount: totalParticleCount * particleRatio
            })
          );
        }

        fire(0.25, {
          startVelocity: 55
        });
        fire(0.2);
        fire(0.35, {
          decay: 0.91,
          scalar: 0.8
        });
        fire(0.1, {
          startVelocity: 25,
          decay: 0.92,
          scalar: 1.2
        });
        fire(0.1, {
          startVelocity: 45
        });
      }
    },
    ...getStepButtons("End tour", undefined, "Restart tour", step => {
      step.complete();
      setTimeout(() => {
        startTour();
      }, 500);
    })
  }
];

/*
  Get steps of the product tour with options to add to new created tour
 */
const getProductTourSteps = startTour => (options, userData, history) => {
  if (options.fromPart === "two") {
    return getPartTwoAddingStopsAndOptimizeSteps().concat(
      getPartThreePublishAndShareSteps()
    );
  } else if (options.fromPart === "three") {
    return getPartThreePublishAndShareSteps();
  }

  return gePartMiscStepOnboardingWelcome(userData, history)
    .concat(getPartOneInitializeNewProjectSteps())
    .concat(getPartTwoAddingStopsAndOptimizeSteps())
    .concat(getPartThreePublishAndShareSteps(userData, history, startTour));
};

const updateUserOnboardingState = newOnboardingState => {
  const {
    auth: { userData }
  } = store.getState();
  store.dispatch(
    setUser({
      ...userData,
      organisationSite: {
        ...userData.organisationSite,
        organisation: {
          ...userData.organisationSite.organisation,
          onboarding: {
            product_tour_part_1_completed: false,
            product_tour_part_2_completed: false,
            product_tour_part_3_completed: false,
            product_tour_completed: false,
            ...(userData.organisationSite.organisation.onboarding || {}),
            ...newOnboardingState
          }
        }
      }
    })
  );
};

/*
  Hook to work with product tour
 */
const useProductTour = () => {
  const tourMethods = useTourMethods();

  const onCancel = useCallback(() => {
    console.log("canceling product tour.");
  }, []);

  /*
    On complete, update the BE about the product tour progress.
   */
  const onComplete = useCallback(async () => {
    console.log("completing product tour.");
    try {
      const data = {
        product_tour_part_1_completed: true,
        product_tour_part_2_completed: true,
        product_tour_part_3_completed: true,
        product_tour_completed: true
      };
      await API.patch("v1/organisations/onboarding", data);
      updateUserOnboardingState(data);
    } catch (error) {
      console.log(error);
    }
  }, []);

  /*
    On showing a step, check and update the BE about the product tour progress.
   */
  const onShow = useCallback(async step => {
    console.log(`Showing product tour step. stepId: ${step.id}`);
    let data = null;

    /*
      If first step of part two shown, set part one completed.
     */
    if (
      step.id ===
      PRODUCT_TOUR_STEP_IDS.PART_ADDING_STOPS_AND_OPTIMIZE_STEP_BRIEF
    ) {
      data = {
        product_tour_part_1_completed: true
      };
    }

    /*
      If first step of part three shown, set part two completed.
     */
    if (step.id === PRODUCT_TOUR_STEP_IDS.PART_PUBLISH_AND_SHARE_STEP_BRIEF) {
      data = {
        product_tour_part_1_completed: true,
        product_tour_part_2_completed: true
      };
    }

    if (data) {
      try {
        await API.patch("v1/organisations/onboarding", data);
        updateUserOnboardingState(data);
      } catch (error) {
        console.log(error);
      }
    }
  }, []);

  /*
    Start new product tour.
   */
  const startTour = useCallback(
    (options = {}) => {
      const startTour = () => {
        if (!tourMethods.hasActiveTourCurrently()) {
          tourMethods.startTour(
            TOUR_TYPES.PRODUCT,
            {
              onCancel,
              onComplete,
              onShow,
              ...options
            },
            getProductTourSteps(startTour)
          );
        }
      };
      startTour();
    },
    [onCancel, onComplete, onShow, tourMethods]
  );

  // Spread the tour methods, so in components just use with useProductTour
  return { ...tourMethods, startTour };
};

export default useProductTour;

export { PRODUCT_TOUR_STEP_IDS, getProductTourSteps };
